import { type ReactElement, cloneElement } from 'react';

import {
	IndexRoute,
	type Middleware,
	type RouteComponent,
	type RouteElement,
	type RouteElementProps,
	RouteGroup,
	type RouteMatches,
	type RouteTransforms,
} from '@shared/router';

interface RouteParams {
	[key: string]: boolean | string | number;
}

export interface RouteDefinition {
	path: string;
	parentPath?: string;
	name?: string;
	isGroup?: boolean;
	isIndex?: boolean;

	before?: Middleware[];
	after?: Middleware[];
	enter?: () => void;
	leave?: () => void;

	component: RouteComponent;
	wrapWith?: RouteComponent;
	wrapGroupWith?: RouteComponent;
	wrapAllWith?: RouteComponent;

	isValid?: boolean;
	params?: RouteParams;
	search?: RouteParams;

	matches?: RouteMatches;
	transforms?: RouteTransforms;

	parentRoute?: RouteDefinition;
	childRoutes?: RouteDefinition[];
}

enum RouteElementType {
	ROUTE = 'Route',
	INDEX = 'IndexRoute',
	GROUP = 'RouteGroup',
}

export interface RouteTree {
	[key: string]: string | RouteTree;
}

export function parser(
	children: RouteElement[]
): [RouteDefinition[], RouteTree] {
	const parse = (
		children: RouteElement[],
		parent?: RouteElement
	): RouteDefinition[] => {
		const parentProps = (parent as ReactElement<RouteElementProps>)?.props;

		const parsed = Array.from(children)
			.map((child: RouteElement) => {
				const props = (child as ReactElement<RouteElementProps>).props;

				if (props?.condition === false) {
					return null;
				}

				const children = props.children;

				let type: RouteElementType = RouteElementType.ROUTE;

				if (child.type === IndexRoute) {
					type = RouteElementType.INDEX;
				} else if (child.type === RouteGroup) {
					type = RouteElementType.GROUP;
				}

				let { path, name, group, before, after, matches, transforms } = props;

				const { enter, leave, wrapWith, component } = props;

				if (!path) {
					path = '/';
				}

				if (!group) {
					group = parentProps?.group || [];
				}

				if (!before) {
					before = [];
				}

				if (!after) {
					after = [];
				}

				if (!matches) {
					matches = {};
				}

				if (!transforms) {
					transforms = {};
				}

				if (parent && parentProps?.path) {
					path = [parentProps.path, path].join('/').replace(/\/+$/, '');
				}

				if (parent && parentProps?.before) {
					before = [...parentProps.before, ...before];
				}

				if (parent && parentProps?.after) {
					after = [...parentProps.after, ...after];
				}

				if (parent && parentProps?.matches) {
					matches = { ...parentProps.matches, ...matches };
				}

				if (parent && parentProps?.transforms) {
					transforms = { ...parentProps.transforms, ...transforms };
				}

				let wrapGroupWith: RouteComponent | undefined = undefined;

				if (parent && parentProps?.wrapWith) {
					wrapGroupWith = parentProps.wrapWith as RouteComponent;
				}

				// Remove leading, trailing and exessive slashes
				path = path
					.replace(/^\/+/, '')
					.replace(/\/+/g, '/')
					.replace(/\/+$/, '');

				// Fix optional parameters in required groups (i.e /users/:id?/foo should be /users/:id/foo)
				if (path.includes('?/')) {
					path = path.replace('?/', '/');
				}

				if (parent && parentProps?.name && parentProps?.isRouteGroup) {
					if (!group.includes(parentProps.name)) {
						group.push(parentProps.name);
					}
				}

				// Generate name based on path and inherited group names
				// This will only apply if a route is *unnamed*
				if (!name) {
					let parts: string[] = path
						.split('/')
						.map((n: string) => n.replace(/:[a-z-_]+[*?]?/, ''))
						.filter((n: string) => n);

					parts = [...new Set(parts)];

					if (parts.length > 1 || type === RouteElementType.INDEX) {
						parts = parts.slice(group.length);
					}

					parts = [...group, ...parts];

					parts = parts.map((part: string) =>
						part.replace(/-([a-z])/g, (m, w) => w.toUpperCase())
					);

					// @NOTE Deliberately excludes the catch-all modifier: a route
					// ending in `:param*` is a section root (`docs`), not a detail
					// view, so it must not be named `…show`.
					if (path.match(/(:[a-z-_]+[?]?)$/)) {
						parts.push('show');
					} else if (type === RouteElementType.INDEX) {
						parts.push('index');
					}

					if (parts.length === 1 && children !== undefined) {
						parts.push('index');
					}

					parts = [...new Set(parts)];

					name = parts.join('.');
				}

				const $route: RouteDefinition = {
					path,
					name,
					component: component as RouteComponent,
					wrapWith: wrapWith as RouteComponent | undefined,
					wrapGroupWith,

					isIndex: type === RouteElementType.INDEX,
					isGroup: type === RouteElementType.GROUP,
					matches,
					transforms,

					before,
					after,
					enter,
					leave,
				};

				if (parentProps?.path && parentProps.path !== path) {
					$route.parentPath = parentProps.path;
				}

				if (children) {
					let parseable = children;

					if (!Array.isArray(children)) {
						parseable = [children];
					}

					const $next: RouteDefinition[] = [
						$route,
						...parse(
							parseable as RouteElement[],
							cloneElement(child as RouteElement, {
								path,
								name,

								component,
								wrapWith: wrapWith || wrapGroupWith,
								group,
								matches,
								transforms,

								before,
								after,
							}) as RouteElement
						),
					];

					return $next;
				}

				return $route;
			})
			.flat() as RouteDefinition[];

		return parsed.filter((route: RouteDefinition) => route && !route.isGroup);
	};

	const parseTree = (routes: RouteDefinition[]): RouteTree => {
		let parsed: RouteTree = routes.reduce((source, route: RouteDefinition) => {
			return {
				...source,
				[route.name as string]: route.path,
			};
		}, {});

		parsed = transformToNestedObject(parsed);

		return parsed;
	};

	const parsed: RouteDefinition[] = parse(children);
	const tree: RouteTree = parseTree(parsed);

	return [parsed, tree];
}

function transformToNestedObject(input: RouteTree): RouteTree {
	const result: Record<string, any> = {};

	for (const [key, value] of Object.entries(input)) {
		const keyParts = key.split('.');

		let current = result;

		for (let i = 0; i < keyParts.length; i++) {
			const part = keyParts[i];

			if (part === undefined) continue;

			if (i === keyParts.length - 1) {
				if (current[part] && typeof current[part] === 'object') {
					throw new Error(
						`Conflict detected: '${key}' tries to overwrite an existing object.`
					);
				}

				current[part] = value;
			} else {
				if (current[part] && typeof current[part] !== 'object') {
					throw new Error(
						`Conflict detected: '${key}' tries to overwrite an existing value.`
					);
				}

				current[part] = current[part] || {};
			}

			current = current[part];
		}
	}

	return result;
}
